import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { 
  Percent, 
  Copy, 
  Trash2, 
  Plus,
  Loader2,
  Check,
  Calendar,
  ToggleLeft,
  ToggleRight,
  Tag
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDiscountCodes({ token }) {
  const { t, i18n } = useTranslation();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Form state
  const [newCode, setNewCode] = useState({
    code: "",
    discount_percent: 10,
    valid_until: "",
    max_uses: "",
    applies_to: "all",
    note: ""
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const res = await fetch(`${API}/admin/discount-codes`, { 
        credentials: "include", 
        headers 
      });

      if (res.ok) {
        const data = await res.json();
        setCodes(data.discount_codes || []);
      }
    } catch (error) {
      console.error("Error fetching discount codes:", error);
    } finally {
      setLoading(false);
    }
  };

  const createCode = async () => {
    if (!newCode.code || !newCode.valid_until) {
      toast.error(t('admin.discounts.validation.codeAndDate'));
      return;
    }

    setCreating(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const payload = {
        code: newCode.code,
        discount_percent: parseInt(newCode.discount_percent),
        valid_until: newCode.valid_until,
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        applies_to: newCode.applies_to,
        note: newCode.note || null
      };

      const res = await fetch(`${API}/admin/discount-code`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        setCodes([data, ...codes]);
        setShowCreateDialog(false);
        setNewCode({
          code: "",
          discount_percent: 10,
          valid_until: "",
          max_uses: "",
          applies_to: "all",
          note: ""
        });
        toast.success(t('admin.discounts.createSuccess', { code: data.code }));
      } else {
        const error = await res.json();
        toast.error(error.detail || t('admin.discounts.createError'));
      }
    } catch (error) {
      toast.error(t('admin.discounts.createError'));
    } finally {
      setCreating(false);
    }
  };

  const copyCode = async (code) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(t('admin.discounts.codeCopied'));
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const deleteCode = async (codeId) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const res = await fetch(`${API}/admin/discount-code/${codeId}`, {
        method: "DELETE",
        credentials: "include",
        headers,
      });

      if (res.ok) {
        setCodes(codes.filter(c => c.code_id !== codeId));
        toast.success(t('admin.discounts.deleteSuccess'));
      }
    } catch (error) {
      toast.error(t('admin.discounts.deleteError'));
    }
  };

  const toggleCode = async (codeId) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const res = await fetch(`${API}/admin/discount-code/${codeId}/toggle`, {
        method: "PATCH",
        credentials: "include",
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        setCodes(codes.map(c => 
          c.code_id === codeId ? { ...c, is_active: data.is_active } : c
        ));
        toast.success(data.is_active ? t('admin.discounts.activated') : t('admin.discounts.deactivated'));
      }
    } catch (error) {
      toast.error(t('admin.discounts.toggleError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'de' ? "de-DE" : "en-US");
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-volt animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Discount Codes Section */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Percent className="h-5 w-5 text-carb-orange" />
            <h2 className="font-display text-xl font-bold uppercase">{t('admin.discounts.title')}</h2>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="skew-btn bg-carb-orange text-black hover:bg-carb-orange/90 font-bold"
            data-testid="create-discount-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span>{t('admin.discounts.createNew')}</span>
          </Button>
        </div>

        {codes.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {t('admin.discounts.noCodes')}
          </p>
        ) : (
          <div className="space-y-3">
            {codes.map((code) => (
              <div 
                key={code.code_id}
                className={`p-4 rounded-lg border ${
                  !code.is_active || isExpired(code.valid_until) 
                    ? 'border-white/10 bg-white/5 opacity-60' 
                    : 'border-carb-orange/30 bg-carb-orange/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-lg font-bold text-carb-orange">
                        {code.code}
                      </span>
                      <span className="text-lg font-bold text-white">
                        -{code.discount_percent}%
                      </span>
                      {code.is_active && !isExpired(code.valid_until) ? (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                          {t('admin.discounts.active')}
                        </span>
                      ) : isExpired(code.valid_until) ? (
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                          {t('admin.discounts.expired')}
                        </span>
                      ) : (
                        <span className="text-xs bg-white/20 text-white/60 px-2 py-0.5 rounded">
                          {t('admin.discounts.inactive')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {t('admin.discounts.validUntil')}: {formatDate(code.valid_until)}
                      </span>
                      <span>
                        {t('admin.discounts.timesUsed')}: {code.times_used}{code.max_uses ? ` / ${code.max_uses}` : '×'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {code.applies_to === "all" ? t('admin.discounts.appliesAll') : code.applies_to === "single" ? t('admin.discounts.appliesSingle') : t('admin.discounts.appliesSubscription')}
                      </span>
                      {code.note && (
                        <span className="text-white/60">"{code.note}"</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyCode(code.code)}
                      className="hover:bg-white/10"
                      data-testid={`copy-code-${code.code_id}`}
                    >
                      {copiedCode === code.code ? (
                        <Check className="h-4 w-4 text-green-400" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCode(code.code_id)}
                      className="hover:bg-white/10"
                      data-testid={`toggle-code-${code.code_id}`}
                    >
                      {code.is_active ? (
                        <ToggleRight className="h-5 w-5 text-green-400" />
                      ) : (
                        <ToggleLeft className="h-5 w-5 text-white/40" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteCode(code.code_id)}
                      className="hover:bg-red-500/10 text-red-400"
                      data-testid={`delete-code-${code.code_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Discount Code Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{t('admin.discounts.createDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="code" className="text-sm text-muted-foreground">
                {t('admin.discounts.createDialog.code')} *
              </Label>
              <Input
                id="code"
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                placeholder={t('admin.discounts.createDialog.codePlaceholder')}
                className="mt-1 bg-white/5 border-white/10 font-mono uppercase"
                data-testid="discount-code-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="discount" className="text-sm text-muted-foreground">
                  {t('admin.discounts.createDialog.discount')} *
                </Label>
                <Input
                  id="discount"
                  type="number"
                  min="1"
                  max="100"
                  value={newCode.discount_percent}
                  onChange={(e) => setNewCode({ ...newCode, discount_percent: e.target.value })}
                  className="mt-1 bg-white/5 border-white/10"
                  data-testid="discount-percent-input"
                />
              </div>
              <div>
                <Label htmlFor="valid_until" className="text-sm text-muted-foreground">
                  {t('admin.discounts.createDialog.validUntil')} *
                </Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={newCode.valid_until}
                  onChange={(e) => setNewCode({ ...newCode, valid_until: e.target.value })}
                  className="mt-1 bg-white/5 border-white/10"
                  data-testid="valid-until-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_uses" className="text-sm text-muted-foreground">
                  {t('admin.discounts.createDialog.maxUses')}
                </Label>
                <Input
                  id="max_uses"
                  type="number"
                  min="1"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                  placeholder={t('admin.discounts.createDialog.unlimited')}
                  className="mt-1 bg-white/5 border-white/10"
                  data-testid="max-uses-input"
                />
              </div>
              <div>
                <Label htmlFor="applies_to" className="text-sm text-muted-foreground">
                  {t('admin.discounts.createDialog.appliesTo')}
                </Label>
                <select
                  id="applies_to"
                  value={newCode.applies_to}
                  onChange={(e) => setNewCode({ ...newCode, applies_to: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-md bg-white/5 border border-white/10 text-white"
                  data-testid="applies-to-select"
                >
                  <option value="all">{t('admin.discounts.appliesAll')}</option>
                  <option value="single">{t('admin.discounts.appliesSingleOnly')}</option>
                  <option value="subscription">{t('admin.discounts.appliesSubscriptionOnly')}</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="note" className="text-sm text-muted-foreground">
                {t('admin.discounts.createDialog.note')}
              </Label>
              <Input
                id="note"
                value={newCode.note}
                onChange={(e) => setNewCode({ ...newCode, note: e.target.value })}
                placeholder={t('admin.discounts.createDialog.notePlaceholder')}
                className="mt-1 bg-white/5 border-white/10"
                data-testid="discount-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-white/20"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={createCode}
              disabled={creating || !newCode.code || !newCode.valid_until}
              className="bg-carb-orange text-black hover:bg-carb-orange/90"
              data-testid="confirm-create-discount"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('admin.discounts.createDialog.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
